import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Gift, History, Ticket, TrendingDown, TrendingUp } from "lucide-react";
import BottomNav from "../components/BottomNav";
import API from "../services/api";

const TABS = [
  { id: "bets",    label: "Bet History",    icon: Ticket },
  { id: "casino",  label: "Casino",         icon: History },
  { id: "dw",      label: "D & W",          icon: TrendingUp },
  { id: "bonus",   label: "Bonus",          icon: Gift },
];

function UserAccount() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const user = useMemo(() => JSON.parse(localStorage.getItem("user") || "null"), []);
  const [activeTab, setActiveTab] = useState(() => {
    const t = searchParams.get("tab");
    return TABS.find((x) => x.id === t) ? t : "bets";
  });
  const [bets, setBets] = useState([]);
  const [txns, setTxns] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user?.id) { navigate("/login"); return; }
    setLoading(true);
    Promise.all([
      API.get(`/bets/${user.id}`).then((r) => setBets(Array.isArray(r.data) ? r.data : [])).catch(() => setBets([])),
      API.get(`/transactions/${user.id}`).then((r) => setTxns(Array.isArray(r.data) ? r.data : [])).catch(() => setTxns([])),
    ]).finally(() => setLoading(false));
  }, [navigate, user?.id]);

  const deposits = useMemo(() => txns.filter((t) => t.type === "Deposit"), [txns]);
  const withdrawals = useMemo(() => txns.filter((t) => t.type === "Withdraw"), [txns]);
  const bonuses = useMemo(() => txns.filter((t) => (t.type || "").toLowerCase().includes("bonus") || (t.type || "").toLowerCase() === "referral"), [txns]);
  const casinoBets = useMemo(() => bets.filter((b) => (b.type || "").toLowerCase().includes("casino")), [bets]);
  const normalBets = useMemo(() => bets.filter((b) => !(b.type || "").toLowerCase().includes("casino")), [bets]);

  const fmt = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`;
  const fmtDate = (d) => {
    if (!d) return "—";
    return new Date(d).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  };

  return (
    <div style={page}>
      {/* Header */}
      <header style={header}>
        <button type="button" style={backBtn} onClick={() => navigate("/profile")}>
          <ArrowLeft size={20} color="#fff" />
        </button>
        <strong style={headerTitle}>My Account</strong>
        <div style={{ width: 36 }} />
      </header>

      {/* User info strip */}
      <div style={userStrip}>
        <div style={avatarWrap}>{String(user?.name || "U").charAt(0).toUpperCase()}</div>
        <div>
          <div style={stripName}>{user?.name || "Player"}</div>
          <div style={stripPhone}>{user?.phone ? `+91 ${String(user.phone).slice(-10)}` : ""}</div>
        </div>
      </div>

      {/* Tab bar */}
      <div style={tabBar}>
        {TABS.map((tab) => {
          const Icon = tab.icon;
          const active = activeTab === tab.id;
          return (
            <button key={tab.id} type="button" style={active ? activeTabBtn : tabBtn} onClick={() => setActiveTab(tab.id)}>
              <Icon size={14} />
              <span>{tab.label}</span>
            </button>
          );
        })}
      </div>

      {/* Content */}
      <main style={content}>
        {loading ? (
          <div style={emptyBox}><div style={spinner} /><span>Loading...</span></div>
        ) : activeTab === "bets" ? (
          <BetList bets={normalBets} fmt={fmt} fmtDate={fmtDate} />
        ) : activeTab === "casino" ? (
          casinoBets.length === 0
            ? <EmptyState label="No casino history yet" />
            : <BetList bets={casinoBets} fmt={fmt} fmtDate={fmtDate} />
        ) : activeTab === "dw" ? (
          <DWList deposits={deposits} withdrawals={withdrawals} fmt={fmt} fmtDate={fmtDate} />
        ) : activeTab === "bonus" ? (
          bonuses.length === 0
            ? <EmptyState label="No bonus history yet" />
            : <TxnList items={bonuses} fmt={fmt} fmtDate={fmtDate} />
        ) : null}
      </main>

      <BottomNav />
    </div>
  );
}

/* ── Sub-components ── */

function BetList({ bets, fmt, fmtDate }) {
  if (!bets.length) return <EmptyState label="No bets yet" />;
  return (
    <div style={list}>
      {bets.map((bet) => {
        const won = bet.status === "won";
        const lost = bet.status === "lost";
        return (
          <div key={bet.id} style={betCard}>
            <div style={betTop}>
              <div style={betLeft}>
                <div style={betMarket}>{bet.market_name || "Market"}</div>
                <div style={betMeta}>{bet.type} · {bet.number} · {fmtDate(bet.created_at)}</div>
              </div>
              <span style={statusPill(bet.status)}>{bet.status || "Pending"}</span>
            </div>
            <div style={betBottom}>
              <span>Bet: <strong>{fmt(bet.amount)}</strong></span>
              {won && bet.payout > 0 && <span style={winAmt}>Won {fmt(bet.payout)}</span>}
              {lost && <span style={lostAmt}>Lost {fmt(bet.amount)}</span>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function DWList({ deposits, withdrawals, fmt, fmtDate }) {
  const [view, setView] = useState("deposit");
  const items = view === "deposit" ? deposits : withdrawals;
  return (
    <div>
      <div style={dwToggle}>
        <button type="button" style={view === "deposit" ? dwActiveBtn : dwBtn} onClick={() => setView("deposit")}>
          <TrendingDown size={13} /> Deposits ({deposits.length})
        </button>
        <button type="button" style={view === "withdraw" ? dwActiveBtn : dwBtn} onClick={() => setView("withdraw")}>
          <TrendingUp size={13} /> Withdrawals ({withdrawals.length})
        </button>
      </div>
      {items.length === 0 ? <EmptyState label={`No ${view}s yet`} /> : (
        <div style={list}>
          {items.map((t) => (
            <div key={`${t.type}-${t.id}`} style={txCard}>
              <div style={txLeft}>
                <div style={{ ...txIcon, background: view === "deposit" ? "#dcfce7" : "#fee2e2" }}>
                  {view === "deposit"
                    ? <TrendingDown size={16} color="#16a34a" />
                    : <TrendingUp size={16} color="#dc2626" />}
                </div>
                <div>
                  <div style={txTitle}>{t.type}</div>
                  <div style={txDate}>{fmtDate(t.created_at)}</div>
                  {t.utr && <div style={txUtr}>UTR: {t.utr}</div>}
                </div>
              </div>
              <div style={txRight}>
                <div style={{ ...txAmt, color: view === "deposit" ? "#16a34a" : "#dc2626" }}>
                  {view === "deposit" ? "+" : "-"}{fmt(t.amount)}
                </div>
                <span style={statusPill(t.status)}>{t.status || "pending"}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function TxnList({ items, fmt, fmtDate }) {
  return (
    <div style={list}>
      {items.map((t) => (
        <div key={`${t.type}-${t.id}`} style={txCard}>
          <div style={txLeft}>
            <div style={{ ...txIcon, background: "#fef3c7" }}>
              <Gift size={16} color="#d97706" />
            </div>
            <div>
              <div style={txTitle}>{t.type}</div>
              <div style={txDate}>{fmtDate(t.created_at)}</div>
              {t.remark && <div style={txUtr}>{t.remark}</div>}
            </div>
          </div>
          <div style={txRight}>
            <div style={{ ...txAmt, color: "#d97706" }}>+{fmt(t.amount)}</div>
            <span style={statusPill(t.status)}>{t.status || "pending"}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

function EmptyState({ label }) {
  return (
    <div style={emptyBox}>
      <div style={emptyIcon}>📋</div>
      <div style={emptyLabel}>{label}</div>
    </div>
  );
}

/* ── Styles ── */
const page = { minHeight: "100vh", background: "#f0f0ff", paddingBottom: 80 };
const header = { height: 56, background: "linear-gradient(135deg,#1e1b4b,#312e81)", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 14px", position: "sticky", top: 0, zIndex: 20 };
const backBtn = { width: 34, height: 34, border: 0, borderRadius: 10, background: "rgba(255,255,255,0.12)", display: "grid", placeItems: "center", cursor: "pointer" };
const headerTitle = { color: "#fff", fontSize: 16, fontWeight: 900 };

const userStrip = { background: "linear-gradient(135deg,#1e1b4b,#4f46e5)", padding: "14px 16px", display: "flex", alignItems: "center", gap: 12 };
const avatarWrap = { width: 44, height: 44, borderRadius: "50%", background: "rgba(255,255,255,0.15)", border: "2px solid rgba(255,255,255,0.3)", display: "grid", placeItems: "center", fontSize: 18, fontWeight: 900, color: "#fff", flexShrink: 0 };
const stripName = { color: "#fff", fontWeight: 800, fontSize: 15 };
const stripPhone = { color: "rgba(255,255,255,0.6)", fontSize: 12, marginTop: 2 };

const tabBar = { display: "flex", background: "#fff", borderBottom: "1.5px solid #e8eaf6", position: "sticky", top: 56, zIndex: 10 };
const tabBtn = { flex: 1, height: 46, border: 0, background: "transparent", color: "#94a3b8", fontWeight: 700, fontSize: 11, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 3, cursor: "pointer", borderBottom: "2.5px solid transparent" };
const activeTabBtn = { ...tabBtn, color: "#4f46e5", borderBottom: "2.5px solid #4f46e5" };

const content = { padding: "12px 12px 0" };
const list = { display: "grid", gap: 10 };

const betCard = { background: "#fff", borderRadius: 14, padding: "12px 14px", boxShadow: "0 2px 8px rgba(79,70,229,0.06)", border: "1.5px solid #e8eaf6" };
const betTop = { display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8, marginBottom: 8 };
const betLeft = { minWidth: 0 };
const betMarket = { fontWeight: 800, color: "#1e1b4b", fontSize: 14, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" };
const betMeta = { color: "#94a3b8", fontSize: 11, marginTop: 2 };
const betBottom = { display: "flex", gap: 12, alignItems: "center", fontSize: 13, color: "#374151" };
const winAmt = { background: "#dcfce7", color: "#16a34a", borderRadius: 8, padding: "3px 8px", fontWeight: 800, fontSize: 12 };
const lostAmt = { color: "#dc2626", fontSize: 12, fontWeight: 700 };

const txCard = { background: "#fff", borderRadius: 14, padding: "12px 14px", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, boxShadow: "0 2px 8px rgba(79,70,229,0.06)", border: "1.5px solid #e8eaf6" };
const txLeft = { display: "flex", alignItems: "center", gap: 10, minWidth: 0 };
const txIcon = { width: 36, height: 36, borderRadius: 10, display: "grid", placeItems: "center", flexShrink: 0 };
const txTitle = { fontWeight: 700, color: "#1e1b4b", fontSize: 13 };
const txDate = { color: "#94a3b8", fontSize: 11, marginTop: 2 };
const txUtr = { color: "#7c3aed", fontSize: 10, marginTop: 2, fontWeight: 600 };
const txRight = { display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4, flexShrink: 0 };
const txAmt = { fontWeight: 900, fontSize: 14 };

const dwToggle = { display: "flex", gap: 8, marginBottom: 12 };
const dwBtn = { flex: 1, height: 38, border: "1.5px solid #e0e7ff", borderRadius: 10, background: "#fff", color: "#64748b", fontWeight: 700, fontSize: 12, display: "flex", alignItems: "center", justifyContent: "center", gap: 5, cursor: "pointer" };
const dwActiveBtn = { ...dwBtn, background: "#eef2ff", borderColor: "#4f46e5", color: "#4f46e5" };

const statusPill = (status) => {
  const s = String(status || "pending").toLowerCase();
  const colors = { approved: ["#dcfce7", "#16a34a"], won: ["#dcfce7", "#16a34a"], pending: ["#fef3c7", "#d97706"], rejected: ["#fee2e2", "#dc2626"], lost: ["#fee2e2", "#dc2626"] };
  const [bg, color] = colors[s] || ["#f1f5f9", "#64748b"];
  return { background: bg, color, borderRadius: 8, padding: "3px 8px", fontSize: 10, fontWeight: 800, textTransform: "capitalize", whiteSpace: "nowrap" };
};

const emptyBox = { padding: "48px 16px", textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center", gap: 10 };
const emptyIcon = { fontSize: 40 };
const emptyLabel = { color: "#94a3b8", fontSize: 14, fontWeight: 600 };
const spinner = { width: 32, height: 32, border: "3px solid #e8eaf6", borderTop: "3px solid #4f46e5", borderRadius: "50%", animation: "spin 0.8s linear infinite" };

export default UserAccount;
